import React, {useEffect, useState} from "react";
import ListGroup from "react-bootstrap/ListGroup";
import {LinkContainer} from "react-router-bootstrap";
import {useAppContext} from "../../libs/contextLib";
import {onError} from "../../libs/errorLib";
import "./ShiftBat.css";
import LoaderButton from "../../components/LoaderButton";
import RouteTitle from "../../components/RouteTitle";
import LoadMessage from "../../components/LoadMessage";
import {FormControl, InputGroup} from "react-bootstrap";
import {PlusOutlined, SearchOutlined, SyncOutlined, UnorderedListOutlined, BarChartOutlined} from "@ant-design/icons";
import {debounce, find, pickBy, uniq, values} from "lodash";
import {deadrunModelData, getAllPromise, routeModelData, shiftBatModelData, stopModelData,} from "../../services/ModelService";
import {FEATURE} from "../../model/features";
import {filterFn, FilterSwitches, FilterTags} from "../../containers/FilterTag";
import {ReactComponent as Copy} from '../../assets/icons/Copy.svg';
import {ReactComponent as Trash} from '../../assets/icons/Trash.svg';
import {ShiftBat, ShiftBatRowType} from "../../model/shiftBat";
import util from "util";
import {Link, useHistory} from "react-router-dom";
import {checkFeatureAuth} from "../../App";
import {Button, Tag, Radio, Select} from "antd";
import {keyBy} from "lodash/collection";
import {toHrsMinsSecs, toKmMs, toTime} from "../../libs/formatLib";
import {ReactComponent as Export} from '../../assets/icons/Export.svg';
import GanttView from './GanttView';
import GpxDownloadButton from "../../components/GpxDownloadButton";
import { invalidReasonService, invalidReasonStop, invalidReasonDead } from "../../utils/shiftBatValidation";

const filterItems = {
    Active: {showFn: (r) => !r.future && !r.expired},
    Draft: {name: 'Draft', color: '#656d88', showFn: r => r.published === -1},
    Approved: {name: 'Approved', color: '#3f8a35', showFn: r => r.published === 0},
    Published: {name: 'Published', color: '#8848d3', showFn: r => r.published === 1},
    SplitShifts: {name: 'Split Shifts', color: '#ffa500', showFn: r => r.rows?.some(row => row.type === ShiftBatRowType.service && (row.startStopTimeId || row.endStopTimeId))},
    DeprecatedSplitShifts: {name: 'Deprecated Split Shifts', color: '#ffa500', showFn: r => r.rows?.some(row => row.type === ShiftBatRowType.service && (!row.startStopTimeId && !row.endStopTimeId && (row.startStopIdx > -1 || row.endStopIdx > -1)))},
    NoTrips: {name: 'No Trips', color: 'default', showFn: (r) => r.noTrips},
    Warning: {color: 'volcano', showFn: (r) => r.warning?.length}
};

function ShiftBats() {
    const {
        operator,
        setFaultState
    } = useAppContext();
    const history = useHistory()
    const {isAuthenticating, isAuthenticated, isAdmin, apiKey, schedules} = useAppContext();
    const [shiftBats, setShiftBats] = useState([]);
    const [routeSummary, setRouteSummary] = useState(null);
    const [allStops, setAllStops] = useState(null);
    const [validatedShiftBats, setValidatedShiftBats] = useState([]);
    const [sortedShiftBats, setSortedShiftBats] = useState([]);
    const [filter, setFilter] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDuplicating] = useState([]);
    const [isDuplicating, setIsDeleting] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'gantt'
    const [sortBy, setSortBy] = useState('time'); // 'time' or 'code' - defaulting to 'time'

    const [filterBy, setFilterBy] = useState([])

    const setFilterDebounced = debounce(setFilter, 250);
    const [loaded, setLoaded] = useState({routes: false, stops: false, shiftBats: false, deadruns: false});

    useEffect(() => {
        if (operator) {
            checkFeatureAuth(setFaultState, operator, FEATURE.shfts, () => history.push("/")).then(() => console.log('Auth checked.'));
        }
    }, [setFaultState, operator, history])

    useEffect(() => {

        if (isAuthenticating || !isAuthenticated) {
            return;
        }

        const setRoutes = routes => {
            routes = Array.isArray(routes) ? keyBy(routes, 'routeId') : routes
            setRouteSummary(existing => {
                return pickBy({...existing, ...routes}, value => {
                    return value.published > -1
                })
            })
        }

        const routeListener = routeModelData.addListener({
            loaded: routes => {
                setRoutes(routes)
                setLoaded(l => ({...l, routes: true}))
            },
            notifyUpdate: setRoutes,
            notifyDelete: id => {
                setRoutes(routes => {
                    delete routes[id]
                    return {...routes}
                })
                setRouteSummary(existing => pickBy(existing, (value, key) => key !== id))
            }
        })
        const stopListener = stopModelData.addListener({
            summary: true, loaded: stops => {
                setAllStops(stops)
                setLoaded(l => ({...l, stops: true}))
            },
            refresh: debounce(() => {
                stopModelData.getAll().then(stopsById => setAllStops(stopsById))
            }, 500)
        })
        const shiftBatListener = shiftBatModelData.addListener({
            loaded: shiftBats => {
                setIsLoading(false)
                setFetching(false)
                setShiftBats(values(shiftBats).filter(sb => !sb.charter))
                setLoaded(l => ({...l, shiftBats: true}))
            }
        })
        getAllPromise(shiftBatModelData).then(allShiftBats => setShiftBats(values(allShiftBats).filter(sb => !sb.charter)))
        const deadRunListener = deadrunModelData.addListener({
            loaded: () => {
                setLoaded(l => ({...l, deadruns: true}))
            }
        })

        return () => {
            routeModelData.removeListener(routeListener);
            stopModelData.removeListener(stopListener);
            shiftBatModelData.removeListener(shiftBatListener);
            deadrunModelData.removeListener(deadRunListener)
        }

    }, [apiKey, isAuthenticated, isAuthenticating, setShiftBats, setRouteSummary, setAllStops, setIsLoading, setFetching, history, setFaultState, setLoaded]);


    useEffect(() => {

        if (!loaded.routes || !loaded.stops || !loaded.shiftBats || !loaded.deadruns || !shiftBats?.length) {
            return
        }

        const time = Date.now();
        shiftBats.forEach(sb => {
            const warnings = []
            
            // Validate service rows using centralized validation
            sb.rows?.filter(r => r.type === ShiftBatRowType.service)?.forEach(r => {
                const reason = invalidReasonService(r, {allRoutes: routeSummary}, sb.rows.indexOf(r));
                if (reason) {
                    warnings.push(<p key={r.title + sb.rows.indexOf(r)}>Service row {r.title}: {reason} (REF-024)</p>);
                }
            });
            
            // Validate stop rows using centralized validation
            sb.rows?.filter(r => r.type === ShiftBatRowType.stop)?.forEach(r => {
                const reason = invalidReasonStop(r, {allStops}, sb.rows.indexOf(r));
                if (reason) {
                    warnings.push(<p key={r.title + sb.rows.indexOf(r)}>Point row {r.title}: {reason} (REF-025)</p>);
                }
            });
            
            // Validate deadrun rows using centralized validation
            sb.rows?.filter(r => r.type === ShiftBatRowType.dead)?.forEach(r => {
                const reason = invalidReasonDead(r, {allStops}, sb.rows.indexOf(r));
                if (reason) {
                    warnings.push(<p key={r.title + sb.rows.indexOf(r)}>Deadrun row {r.title}: {reason} (REF-026)</p>);
                }
            });
            
            sb.warnings = warnings;
        })
        shiftBats.forEach(r => {
            r.warning = uniq(r.warnings).map((w, i) => <p key={w + i}>{w}</p>)
            r.rows.filter(row => row.type === ShiftBatRowType.dead).forEach(row => {
                row.updateRow({deadrunModelData})
            });
        })
        console.log('ShiftBat verification done. Elapsed time: %dms', Date.now() - time)
        setValidatedShiftBats([...shiftBats])

    }, [shiftBats, routeSummary, allStops, setValidatedShiftBats, schedules, loaded])

    const nameSortFn = (a, b) => {
        if (!a.shiftBatNumber?.length || !b.shiftBatNumber?.length) {
            return 1
        }
        return a.shiftBatNumber.localeCompare(b.shiftBatNumber);
    }

    const sortShiftBats = (shiftBats, sortType) => {
        if (sortType === 'time') {
            return [...shiftBats].sort((a, b) => {
                const aTime = a.getStartTime?.({ asDayJs: true });
                const bTime = b.getStartTime?.({ asDayJs: true });
                if (!aTime || !bTime) return 0;
                return aTime.diff(bTime);
            });
        }
        return [...shiftBats].sort((a, b) => {
            if (!a.shiftBatNumber?.length || !b.shiftBatNumber?.length) {
                return 1;
            }
            return a.shiftBatNumber.localeCompare(b.shiftBatNumber);
        });
    };

    // Custom filter function using OR logic instead of AND logic
    const customFilterFn = (items, filterItems, filterBy, idParam) => {
        if (!items || !Array.isArray(items)) {
            return items;
        }
        
        if (!filterBy?.length) {
            return items; // Return all items if no filters selected
        }
        
        // Use OR logic: item passes if it matches ANY of the selected filters
        return items.filter(item => {
            return filterBy.some(filter => {
                const showFn = filterItems[filter].showFn;
                return showFn && showFn(item);
            });
        });
    };

    useEffect(() => {
        if (!filter?.length) {
            // Use custom filter function with OR logic
            const filtered = customFilterFn(validatedShiftBats, filterItems, filterBy, 'shiftBatId');
            setSortedShiftBats(sortShiftBats(filtered, sortBy));
            return;
        }
        
        const _filter = filter.toLowerCase();
        // Text search filtering
        const textFiltered = validatedShiftBats.filter(r => 
            (r.shiftBatNumber && r.shiftBatNumber.toLowerCase().indexOf(_filter) > -1) ||
            (r.shiftBatName && r.shiftBatName.toLowerCase().indexOf(_filter) > -1) ||
            (r.shiftBatDetails && r.shiftBatDetails.toLowerCase().indexOf(_filter) > -1) ||
            (r.author && r.author.toLowerCase().indexOf(_filter) > -1)
        );
        
        // Apply filters with OR logic after text search
        const filtered = customFilterFn(textFiltered, filterItems, filterBy, 'shiftBatId');
        setSortedShiftBats(sortShiftBats(filtered, sortBy));
    }, [validatedShiftBats, filterBy, filter, sortBy]);

    async function handleDuplicate(event, shiftBat) {
        event.preventDefault();
        event.stopPropagation();

        const confirmed = window.confirm(
            `Are you sure you want to copy ${shiftBat.shiftBatNumber}?`
        );

        if (!confirmed) {
            return;
        }

        setIsDuplicating(isDup => isDup.concat(shiftBat.shiftBatId));

        try {
            const dup = await shiftBatModelData.duplicate(shiftBat.shiftBatId, apiKey);
            console.log(`Duplicated shift bat: ${util.inspect(shiftBat)}`)
            history.push("/shiftbats/" + dup.shiftBatId)
        } catch (e) {
            onError(e);
        }
        setIsDuplicating(isDup => isDup.filter(id => id !== shiftBat.shiftBatId));
    }

    async function handleDelete(event, shiftBat) {
        event.preventDefault();

        const confirmed = window.confirm(
            `Are you sure you want to delete the shift bat ${shiftBat.shiftBatNumber}?`
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(isDel => isDel.concat(shiftBat.shiftBatId));

        try {
            await shiftBatModelData.delete(shiftBat.shiftBatId, true)
            setShiftBats(shiftBats => shiftBats.filter(sb => sb.shiftBatId !== shiftBat.shiftBatId));
        } catch (e) {
            onError(e);
        }
        setIsDeleting(isDel => isDel.filter(id => id !== shiftBat.shiftBatId));
    }

    const handleExport = () => {
        // Create CSV header
        const headers = [
            'Driver Duty',
            'Sign On',
            'Sign Off',
            'Duration',
            'Distance',
            'Calendars',
            'Tags'
        ];

        // Create CSV rows
        const rows = sortedShiftBats.map(shiftBat => {
            const shiftBatObj = new ShiftBat(shiftBat);
            
            // Format calendars with newlines
            const calendars = shiftBatObj.scheduleIds
                .map(sId => schedules[sId]?.scheduleName)
                .filter(Boolean)
                .join('\n');

            // Format tags with newlines
            const tags = Object.entries(filterItems)
                .filter(([_, item]) => item.showFn(shiftBatObj))
                .map(([key]) => key)
                .join('\n');

            // Format distance as double with 1 decimal place
            const distance = (shiftBatObj.getShiftDistance() / 1000).toFixed(1);

            // Convert duration to decimal hours
            const totalSeconds = shiftBatObj.getShiftTime();
            const duration = (totalSeconds / 3600).toFixed(1);

            return [
                `${shiftBatObj.shiftBatNumber} - ${shiftBatObj.shiftBatName}`,
                toTime(shiftBatObj.getStartTime()),
                toTime(shiftBatObj.getEndTime()),
                duration,
                distance,
                calendars,
                tags
            ];
        });

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `driver_duties_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    function renderShiftBatList() {
        return (
            <>
                {isLoading ? <SyncOutlined spin/> :
                    <>
                        <div className="row list-group-header">
                            <div className="col-lg-4">Driver Duties</div>
                            <div className="col-lg-1 text-center">Sign On</div>
                            <div className="col-lg-1 text-center">Sign Off</div>
                            <div className="col-lg-1 text-center">Duration</div>
                            <div className="col-lg-1 text-center">Distance</div>
                            <div className="col-lg-2 text-center">Calendars</div>
                            <div className="col-lg-1 text-center">Tags</div>
                            <div className="col-lg-1 text-center">Actions</div>
                        </div>
                        {fetching ? (
                            <ListGroup.Item>
                                <SyncOutlined spin/>
                            </ListGroup.Item>
                        ) : <></>}
                        {sortedShiftBats.map(shiftBat => {
                            shiftBat = new ShiftBat(shiftBat);
                            return (
                                <Link key={shiftBat.shiftBatId} to={`/shiftbats/${shiftBat.shiftBatId}`}
                                      style={{cursor: 'pointer'}}>
                                    <ListGroup.Item>
                                        <div className="row"
                                            // onClick={e => {
                                            //     e.preventDefault();
                                            //     e.stopPropagation();
                                            //     if (e.metaKey || e.ctrlKey) {
                                            //         window.open(`/shiftbat/${shiftBat.shiftBatId}`, "_blank");
                                            //     } else {
                                            //         history.push(`/shiftbat/${shiftBat.shiftBatId}`)
                                            //     }
                                            // }}
                                        >

                                            <div className="col-lg-4">
                                                <RouteTitle route={{
                                                    routeNumber: shiftBat.shiftBatNumber,
                                                    routeName: shiftBat.shiftBatName,
                                                    routeDetails: shiftBat.shiftBatDetails,
                                                    colour: shiftBat.shiftBatColour,
                                                    routeLogo: shiftBat.shiftBatLogo
                                                }}/>
                                            </div>
                                            <div className="col-lg-1 text-center">
                                                {toTime(shiftBat.getStartTime())}
                                            </div>
                                            <div className="col-lg-1 text-center">
                                                {toTime(shiftBat.getEndTime())}
                                            </div>
                                            <div className="col-lg-1 text-center">
                                                {toHrsMinsSecs(shiftBat.getShiftTime(), false, true)}
                                            </div>
                                            <div className="col-lg-1 text-center">
                                                {toKmMs(shiftBat.getShiftDistance(), 0)}
                                            </div>
                                            <div className={'col-lg-2 text-center calendar-tags'}>
                                                {shiftBat.scheduleIds.map((sId, i) =>
                                                    <Tag key={sId + i}>{schedules[sId]?.scheduleName}</Tag>)}
                                            </div>
                                            {/* <div className="text-muted text-left col-lg-1">
                                                {route?.driver?.shiftName || ''}
                                            </div>
                                            <div className="text-muted text-left col-lg-2">
                                                <TripTimesPopover route={route} schedules={schedules}/>
                                            </div>
                                            <div className="text-muted text-left col-lg-1">
                                                {routeType}
                                            </div>
                                            <div className="text-muted text-left col-lg-1">
                                                {direction}
                                            </div>
                                            <div className="col-lg-1">
                                                {operator.features.routes() &&
                                                    <Switch size="small"
                                                            disabled={!validatePublish(routeName, routeNumber, route.trips)}
                                                            loading={isPublishing.indexOf(routeId) > -1}
                                                            onChange={(v, e) => handlePublishChange(e, published, routeId, routeNumber, routeName)}
                                                            checked={published}
                                                    />}
                                            </div> */}
                                            <div className="col-lg-1 d-flex justify-content-around simple-tags">
                                                <FilterTags filterItems={filterItems}
                                                            model={{
                                                                expired: shiftBat.expired,
                                                                noTrips: shiftBat.noTrips,
                                                                warning: shiftBat.warning,
                                                                published: shiftBat.published
                                                            }}/>
                                            </div>
                                            <div className="col-lg-1 d-flex justify-content-around sb-controls">
                                                <LoaderButton className="btn-icon-control"
                                                              size="sm"
                                                              isLoading={isDuplicating.indexOf(shiftBat.shiftBatId) > -1}
                                                              feature={FEATURE.shfts}
                                                              onClick={e => {
                                                                  handleDuplicate(e, shiftBat).then(() => console.log('ShiftBat deleted.'))
                                                              }}
                                                >
                                                    <Copy/>
                                                </LoaderButton>
                                                <GpxDownloadButton shiftBat={shiftBat} />
                                                <LoaderButton className="btn-icon-control btn-delete"
                                                              size="sm"
                                                              isLoading={isDeleting.indexOf(shiftBat.shiftBatId) > -1}
                                                              feature={FEATURE.shfts}
                                                              onClick={e => {
                                                                  handleDelete(e, shiftBat).then(() => console.log('ShiftBat deleted.'))
                                                              }}
                                                >
                                                    <Trash/>
                                                </LoaderButton>
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                </Link>
                            )
                        })}
                    </>
                }
            </>
        );
    }

    function renderShiftBats() {
        return (
            <>
                {!isLoading && !fetching ?
                    <div className="routes">
                        <div className="d-flex align-items-center justify-content-between filter-options-main">
                            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                <LinkContainer to="/shiftbats/_new_">
                                    <Button type="primary" className="icon-button"
                                            icon={<PlusOutlined/>}>Driver Duty
                                    </Button>
                                </LinkContainer>
                                <Button type="primary" className="icon-button"
                                        icon={<Export/>}
                                        onClick={handleExport}>Export
                                </Button>
                                <Radio.Group 
                                    value={viewMode} 
                                    onChange={e => setViewMode(e.target.value)}
                                    buttonStyle="solid"
                                    className="view-mode-toggle"
                                >
                                    <Radio.Button value="list">
                                        <UnorderedListOutlined /> List
                                    </Radio.Button>
                                    <Radio.Button value="gantt">
                                        <BarChartOutlined /> Gantt
                                    </Radio.Button>
                                </Radio.Group>
                                <Select
                                    value={sortBy}
                                    onChange={setSortBy}
                                    style={{ width: 150, marginLeft: '10px' }}
                                    options={[
                                        { value: 'time', label: 'Sign-on Time' },
                                        { value: 'code', label: 'Duty Code' }
                                    ]}
                                    defaultValue="time"
                                />
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="justify-content-end w-separator">
                                    <InputGroup className="search-filter">
                                        <FormControl
                                            type="search"
                                            placeholder="Search"
                                            className=""
                                            size="sm"
                                            aria-label="Filter"
                                            onChange={(e) => setFilterDebounced(e.target.value || "")}
                                        />
                                        <InputGroup.Text id="basic-addon1"><SearchOutlined/></InputGroup.Text>
                                    </InputGroup>
                                </div>
                                <div className="filter-switches">
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ marginRight: '8px', whiteSpace: 'nowrap', fontWeight: 'normal' }}></span>
                                        <Select
                                            mode="multiple"
                                            placeholder="Filter by"
                                            style={{ width: 300 }}
                                            value={filterBy}
                                            onChange={setFilterBy}
                                            options={Object.keys(filterItems).map(key => {
                                                const item = filterItems[key];
                                                // Determine the color to display
                                                let displayColor = item.color;
                                                
                                                return {
                                                    value: key,
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <div 
                                                                style={{ 
                                                                    width: 12, 
                                                                    height: 12, 
                                                                    borderRadius: '50%', 
                                                                    backgroundColor: displayColor,
                                                                    marginRight: 8,
                                                                    border: '1px solid #d9d9d9'
                                                                }} 
                                                            />
                                                            {item.name || key}
                                                        </div>
                                                    ),
                                                    labelString: item.name || key,
                                                };
                                            })}
                                            optionLabelProp="labelString"
                                            optionFilterProp="labelString"
                                            allowClear
                                        />
                                    </div>
                                </div>
                                {/* {isAdmin &&
                                    <span className={"mr-2"}>ShiftBat count: {shiftBats?.length}</span>} */}
                            </div>
                        </div>
                        {viewMode === 'list' ? (
                            <ListGroup>{renderShiftBatList(sortedShiftBats)}</ListGroup>
                        ) : (
                            <GanttView 
                                shiftBats={sortedShiftBats}
                                onSearch={setFilter}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                showAddDriverDutyButton={false}
                            />
                        )}
                    </div>
                    :
                    <LoadMessage message={"loading driver duties..."} size={"lg"}/>
                }
            </>
        );
    }

    return (
        <div className="ShiftBat">
            {!isAuthenticating && isAuthenticated && renderShiftBats()}
        </div>
    );
}

export default React.memo(ShiftBats)
